
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import ReactImageCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { getCorrectedImageBlob } from '../utils/imageOrientation';

const MeaUpdateAvatarModal = forwardRef((props, ref) => {
  const { currentMembership } = useAuthContext();
  const params = useParams();
  const schoolId = params?.schoolId || currentMembership?.school?.id;
  const accessToken = "";
  const [active, setActive] = useState(false);
  const [callbackFn, setCallbackFn] = useState(() => { });
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const toast = useRef(null);

  useImperativeHandle(ref, () => ({
    show: (callback) => {
      setCallbackFn(() => callback);
      setActive(true);
    },
  }));

  const resetCropState = () => {
    setImageSrc(null);
    setCrop(undefined);
    setCompletedCrop(null);
    setPendingFile(null);
    setIsPreparingImage(false);
  };

  const handleHide = () => {
    setActive(false);
    resetCropState();
  };

  const triggerAvatarPicker = () => {
    fileInputRef.current?.click();
  };

  const handleImageLoad = (event) => {
    const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1,
        width,
        height
      ),
      width,
      height
    );
    setCrop(initialCrop);
  };

  const handleFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    setIsPreparingImage(true);
    setImageSrc(null);
    setPendingFile(null);
    setCrop(undefined);
    setCompletedCrop(null);

    try {
      const correctedBlob = await getCorrectedImageBlob(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setPendingFile(new File([correctedBlob], file.name, { type: correctedBlob.type || file.type || 'image/jpeg' }));
        setIsPreparingImage(false);
      };
      reader.onerror = () => {
        setIsPreparingImage(false);
        toast.current.show({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de lire l'image sélectionnée.",
        });
      };
      reader.readAsDataURL(correctedBlob);
    } catch (err) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setPendingFile(file);
        setIsPreparingImage(false);
      };
      reader.onerror = () => {
        setIsPreparingImage(false);
        toast.current.show({
          severity: "error",
          summary: "Erreur",
          detail: "Impossible de lire l'image sélectionnée.",
        });
      };
      reader.readAsDataURL(file);
    }

    event.target.value = "";
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imageRef.current || !pendingFile) {
      return;
    }

    try {
      const image = imageRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const canvas = document.createElement("canvas");
      const pixelRatio = window.devicePixelRatio || 1;

      const width = completedCrop.width * scaleX;
      const height = completedCrop.height * scaleY;

      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Could not get 2D context");
      }

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        width,
        height,
        0,
        0,
        width,
        height
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          toast.current.show({
            severity: "error",
            summary: "Erreur",
            detail: "Impossible de générer l'image recadrée.",
          });
          return;
        }

        const croppedFile = new File([blob], pendingFile.name, { type: "image/jpeg" });
        props.serviceAPI
          .UpdateAvatar(schoolId, accessToken, props.id, croppedFile)
          .then(() => {
            toast.current.show({
              severity: "success",
              summary: "Succès",
              detail: "Avatar mis a jour!",
            });
            handleHide();
            callbackFn();
          })
          .catch(() => {
            toast.current.show({
              severity: "error",
              summary: "Erreur",
              detail: "Erreur de modification!",
            });
          });
      }, "image/jpeg", 0.95);
    } catch (err) {
      props.serviceAPI
        .UpdateAvatar(schoolId, accessToken, props.id, pendingFile)
        .then(() => {
          toast.current.show({
            severity: "success",
            summary: "Succès",
            detail: "Avatar mis a jour!",
          });
          handleHide();
          callbackFn();
        })
        .catch(() => {
          toast.current.show({
            severity: "error",
            summary: "Erreur",
            detail: "Erreur de modification!",
          });
        });
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Modifier la photo"
        visible={active}
        modal
        style={{ width: "90vw", maxWidth: "700px" }}
        breakpoints={{ "960px": "90vw", "641px": "100vw" }}
        onHide={handleHide}
        contentStyle={{ paddingBottom: 0 }}
      >
        <div className="flex flex-column gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />

          {isPreparingImage ? (
            <div className="flex flex-column align-items-center justify-content-center gap-3 py-5">
              <i className="pi pi-spinner pi-spin" style={{ fontSize: '2.25rem', color: '#3b82f6' }}></i>
              <div className="text-center text-700 font-medium">Chargement de l'image...</div>
              <div className="text-center text-500">Préparation de la photo pour le recadrage.</div>
            </div>
          ) : imageSrc ? (
            <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", background: "#fff", padding: 0, minHeight: 0 }}>
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", background: "linear-gradient(180deg, #fafbfc 0%, #f5f7fb 100%)", border: "1px solid #dbe3f1", borderRadius: "0.75rem", padding: "1rem", width: "100%", maxHeight: "70vh", overflow: "auto" }}>
                  <ReactImageCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop={false}
                    minWidth={50}
                    minHeight={50}
                  >
                    <img
                      ref={imageRef}
                      src={imageSrc}
                      alt="Crop preview"
                      onLoad={handleImageLoad}
                      style={{ width: "100%", height: "auto", display: "block" }}
                    />
                  </ReactImageCrop>
                </div>
                <div style={{ fontSize: "0.85rem", color: "#64748b", textAlign: "center", fontStyle: "italic", paddingTop: "0.75rem" }}>Déplacez et redimensionnez le carré pour sélectionner la zone à conserver</div>
              </div>
            </div>
          ) : (
            <div className="flex flex-column align-items-center justify-content-center gap-3 py-5">
              <i className="pi pi-image" style={{ fontSize: '3rem', color: '#94a3b8' }}></i>
              <div className="text-center text-600">Choisissez une image à recadrer avant la mise à jour de la photo.</div>
              <Button label="Choisir une image" icon="pi pi-upload" onClick={triggerAvatarPicker} disabled={isPreparingImage} />
            </div>
          )}

          <div className="flex gap-2 justify-content-end pt-2 pb-3">
            <Button label="Annuler" icon="pi pi-times" outlined onClick={handleHide} />
            <Button label="Valider et télécharger" icon="pi pi-check" onClick={handleCropComplete} disabled={!imageSrc || isPreparingImage} />
          </div>
        </div>
      </Dialog>
    </>
  )
});

export default MeaUpdateAvatarModal;